/**
 * Development Councils Translations (English)
 * @module developmentCouncils
 * @language en
 */

export const developmentCouncils = {
  developmentCouncils: {
    title: "JCI Development Councils",
    subtitle: "Four Regional Pillars for Growth & Development",
    description: "JCI Area Development Councils work alongside JCI Board of Directors and JCI Headquarters to serve as regional pillars for leadership development, organizational growth and sustainable impact across the globe.",

    heroBadge: "JCI Global Expansion",
    networkGrowthBadge: "JCI Network Growth",

    overview: {
      title: "Global Overview",
      description: "These councils represent the strategic geographic divisions of JCI, working together to develop leaders for a changing world.",
      councilsCount: "4 Area Councils",
      regionsServed: "All Continents"
    },

    sections: {
      mission: "Mission",
      strategicGoals: "2025 Strategic Goals",
      keyAchievements: "Key Achievements",
      keyMetrics: "Key Metrics",
      recommendations: "2026 Recommendations",
      highlights: "2025 Highlights"
    },

    councils: {
      adc: {
        name: "American Development Council",
        abbreviation: "ADC",
        region: "America",
        mission: "Showcase actions that increase JCI brand visibility, boost membership growth and retention, and provide global development leadership experiences to members.",
        goals: {
          brandAwareness: {
            name: "Brand Awareness and Visibility",
            description: "Increase engagement on social media platforms and promote JCI activities.",
            achievements: [
              "30% increase in social media engagement",
              "2 articles published in JCI Lead Magazine",
              "Strategic tags and improved interactions"
            ]
          },
          leadershipDevelopment: {
            name: "Leadership Development",
            description: "Train and develop new leaders across the Americas region.",
            achievements: [
              "National Presidents Meeting with 68% participation (17 Presidents)",
              "American Leadership Academy 25th anniversary with 77% participation",
              "27 new leaders trained over 30+ hours of content",
              "Leadership Training Program with 8 sessions"
            ]
          },
          sustainableStructure: {
            name: "Open, Efficient and Sustainable Structure",
            description: "Support National Organizations development and expansion.",
            achievements: [
              "Chile presented as JCI Potential Organization",
              "Guatemala supported toward Provisional status",
              "Junior Club Conference with 250 registrations"
            ]
          }
        },
        metrics: {
          socialMedia: { label: "Social Media Growth", value: "30%" },
          membership: { label: "Membership Target", value: "5%" },
          bestPractices: { label: "Best Practices Survey", value: "834" },
          leadersTrained: { label: "Leaders Trained", value: "27" }
        },
        highlights: [
          "American Leadership Academy celebrated 25th anniversary",
          "First Online Global Event on Sharing Best Practices",
          "Junior Club Conference launch with ADC leadership"
        ]
      },
      edc: {
        name: "European Development Council",
        abbreviation: "EDC",
        region: "Europe",
        mission: "Foster a culture supporting growth, innovation, and collaboration among National Organizations across 41 regions in Europe.",
        goals: {
          valueProposition: {
            name: "Value Proposition",
            description: "Create quarterly social media campaigns targeting 18-40 demographic.",
            achievements: [
              "+8,896 new followers from January-November",
              "9,789 posts published",
              "5,176 reshares achieved"
            ]
          },
          geographicExpansion: {
            name: "Geographic Expansion",
            description: "Establish JCI presence in new regions.",
            achievements: [
              "Target: 3 new geographic regions by Q3",
              "Supporting potential new organizations",
              "Expanding within existing National Organizations"
            ]
          },
          knowledgeSharing: {
            name: "Knowledge Sharing",
            description: "Organize programs enabling cross-collaboration.",
            achievements: [
              "4 knowledge-sharing programs organized",
              "Mentoring program refreshed with ASE collaboration",
              "24 mentee teams enrolled"
            ]
          },
          productDevelopment: {
            name: "New Product Development",
            description: "Launch tools for developing sustainable local chapters.",
            achievements: [
              "JCI Serious Game developed and tested",
              "7 countries participated in prototype testing",
              "4.5/5 average satisfaction score"
            ]
          }
        },
        metrics: {
          followers: { label: "New Followers", value: "+8,896" },
          academyParticipants: { label: "Academy Participants", value: "158+" },
          gameSatisfaction: { label: "Game Satisfaction", value: "4.5/5" },
          regionsCovered: { label: "Regions Covered", value: "41" }
        },
        highlights: [
          "JCI Serious Game prototype tested across 7 countries",
          "Mediterranean Leadership Academy (31 participants)",
          "European Academy in Sweden (99 participants)",
          "Tri Nations Academy (Ireland, UK, Scotland)",
          "Balkan Academy (7 countries, 14 participants)"
        ]
      },
      amdec: {
        name: "Africa and Middle East Development Council",
        abbreviation: "AMDEC",
        region: "Africa and the Middle East",
        mission: "Empowering young enterprising leaders in Africa and the Middle East to drive positive change through leadership development, strategic partnerships, and innovative solutions.",
        goals: {
          anniversary: {
            name: "40th Anniversary Celebrations",
            description: "Mark four decades of impact and leadership across the region.",
            achievements: [
              "40 Years of Impact digital campaign launched",
              "Recognition ceremonies for past and present leaders",
              "Initiatives launched to sustain AMDEC legacy"
            ]
          },
          skillsDevelopment: {
            name: "Training and Skills Development",
            description: "Implement monthly trainings on key operational areas.",
            achievements: [
              "Financial Management and Budgeting training",
              "Effective Meetings and Parliamentary Procedures",
              "Project Management and Impact Evaluation",
              "Personal Branding and Leadership Development"
            ]
          },
          strategicCollaboration: {
            name: "Strategic Collaboration",
            description: "Bridge language gaps and foster regional collaboration.",
            achievements: [
              "JCI Lusophone Series launched for Portuguese-speaking countries",
              "Junior Club Conference with 100+ online attendees",
              "JCI Game Series in partnership with EDC and JCI France"
            ]
          }
        },
        metrics: {
          yearsOfImpact: { label: "Years of Impact", value: "40" },
          newMarkets: { label: "New Markets", value: "2" },
          conferenceAttendees: { label: "Conference Attendees", value: "100+" },
          trainingTopics: { label: "Training Topics", value: "4+" }
        },
        highlights: [
          "40th Anniversary celebrations across the region",
          "National Presidents Meeting in Mauritius",
          "27th JCI Africa and Middle East Academy",
          "JCI Lusophone Series bridging Portuguese-speaking nations",
          "Kenya completed government registration milestone"
        ]
      },
      apdc: {
        name: "Asia Pacific Development Council",
        abbreviation: "APDC",
        region: "Asia and the Pacific",
        mission: "Expanding JCI presence and acting as a positive factor in regional development through the JCI mission.",
        goals: {
          strategicPlan: {
            name: "Strategic Plan Execution",
            description: "Share and execute JCI Action Framework and 2023-2027 Strategic Plan.",
            achievements: [
              "12 missions conducted to target countries",
              "Collaboration with other Development Councils",
              "Structured plan through quarterly meetings"
            ]
          },
          countryEmpowerment: {
            name: "Country Empowerment",
            description: "Empower assigned countries toward affiliation or graduation.",
            achievements: [
              "6 new Local Organizations affiliated",
              "1 National Organization declared",
              "356+ members recruited",
              "Sustainable foundations in administration and finance"
            ]
          },
          countryDevelopment: {
            name: "Country-Specific Development",
            description: "Targeted development programs for each country.",
            achievements: [
              "Thailand: 20% membership growth (150→180)",
              "Myanmar: Earthquake Relief raising 11,000 USD",
              "Sri Lanka: First National Convention after revival",
              "Uzbekistan: Growth from 46 to 60 members"
            ]
          }
        },
        metrics: {
          membersRecruited: { label: "Members Recruited", value: "356+" },
          newLocalOrgs: { label: "New Local Orgs", value: "6" },
          missionsConducted: { label: "Missions Conducted", value: "12" },
          nationalOrgs: { label: "National Orgs", value: "1" }
        },
        highlights: [
          "JCI Thailand achieved 85% Sustainability Performance Index",
          "Myanmar Earthquake Relief Project helped 500+ individuals",
          "Sri Lanka conducted first National Convention",
          "Timor-Leste connected with 9 partner organizations",
          "Laos development initiated with YEAL partnership"
        ]
      }
    },

    comparativeAnalysis: {
      title: "Comparative Analysis",
      subtitle: "How the four councils compare across key dimensions",
      categories: {
        geographicCoverage: {
          name: "Geographic Coverage",
          adc: "Central & South America, Caribbean, North America",
          edc: "41 regions, concentrated in university centers",
          amdec: "40+ years of regional presence",
          apdc: "Myanmar, Thailand, Sri Lanka, Timor-Leste, Uzbekistan, Laos"
        },
        strategicPriorities: {
          name: "Strategic Priorities",
          adc: "Brand visibility through social media, membership growth targets",
          edc: "Geographic expansion, knowledge-sharing, innovative products",
          amdec: "Institutional legacy, foundational structures, new markets",
          apdc: "Country empowerment, targeted missions, capacity building"
        },
        leadershipDevelopment: {
          name: "Leadership Development",
          adc: "National Presidents Meetings, American Leadership Academy",
          edc: "Regional academies, mentoring programs, serious games",
          amdec: "Leadership Academies, monthly trainings, mentorship",
          apdc: "Country-specific missions, specialized workshops"
        }
      }
    },

    keyHighlights: {
      title: "2025 Key Highlights",
      subtitle: "Major achievements and innovations across all councils",
      items: {
        seriousGame: {
          title: "JCI Serious Game",
          description: "Interactive learning tool developed by EDC, tested across 7 countries with 4.5/5 satisfaction rating."
        },
        juniorClub: {
          title: "Junior Club Conference",
          description: "Cross-council initiative with 250+ registrations, inspiring young aspiring JCI members."
        },
        anniversary: {
          title: "AMDEC 40th Anniversary",
          description: "Four decades of impact celebrated with digital campaigns and recognition ceremonies."
        },
        apdcGrowth: {
          title: "APDC Growth Milestone",
          description: "356+ members recruited, 6 new Local Organizations affiliated, 12 strategic missions conducted."
        },
        lusophoneSeries: {
          title: "JCI Lusophone Series",
          description: "Bridging Portuguese-speaking countries including Angola, Cape Verde, Guinea Bissau, and more."
        }
      }
    },

    newNationalOrganizations: {
      title: "New National Organizations",
      subtitle: "Growing our global network with emerging JCI communities",
      potential: {
        title: "Potential",
        description: "Exploring JCI affiliation and establishing foundational structures"
      },
      provisional: {
        title: "Provisional",
        description: "Building membership and meeting JCI standards for full affiliation"
      },
      fullAffiliation: {
        title: "Full Affiliation",
        description: "Recently achieved full member status within the JCI global network"
      },
      countries: {
        angola: "JCI Angola",
        djibouti: "JCI Djibouti",
        chile: "JCI Chile",
        gambia: "JCI Gambia",
        sriLanka: "JCI Sri Lanka",
        portugal: "JCI Portugal",
        myanmar: "JCI Myanmar",
        armenia: "JCI Armenia"
      }
    },

    chairperson: "Chairperson",
    learnMore: "Learn More",
    viewDetails: "View Details",
    source: "Source: JCI Development Councils 2025 Comprehensive Report"
  }
} as const;
